import { Card, Form, Modal, Button } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useEffect, useRef, useState } from "react";
import WebService from "../../utility/WebService";
import { PencilFill, TrashFill, ToggleOn, ToggleOff } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import AddEmailTemplate from "./AddEmailTemplates";
import CustomTableSortable from "../../components/CustomTable/CustomTableSortable";
import HelperService from "../../utility/HelperService";

const columns = [
  { id: "templateName", label: "Template Name", minWidth: 170, sortable: true },
  { id: "subject", label: "Subject", minWidth: 170, sortable: true },
  // { id: "code", label: "Code", minWidth: 100, sortable: true },
  { id: "createdAt", label: "Created Date", minWidth: 170, sortable: true },
  {
    id: "action",
    label: "Action",
    minWidth: 100,
    align: "center" as const,
  },
];

const EmailTemplates = () => {
  const [showForm, setShowForm] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  const [templateToEdit, setTemplateToEdit] = useState<any | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusItem, setStatusItem] = useState<any | null>(null);

  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [search, setSearch] = useState("");
  const searchRef = useRef<string>("");
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [uiSortField, setUiSortField] = useState<string>("createdAt");
  const [loading, setLoading] = useState<boolean>(false);

  const sortFieldMap: Record<string, string> = {
    createdAt: "createdAt",
    subject: "subject",
    code: "code",
    templateName: "templateName",
  };

  const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });

  useEffect(() => {
    getEmailTemplates(searchRef.current);
  }, [currentPage, sortField, sortOrder]);

  const getEmailTemplates = (keyword = "") => {
    setLoading(true);

    const params = new URLSearchParams();
    params.append("page", currentPage.toString());
    params.append("limit", itemsPerPage.toString());
    params.append("keyword", keyword);
    params.append("sortField", sortField);
    params.append("sortOrder", sortOrder);
    // Show both ACTIVE and INACTIVE (only DELETED is hidden) so a disabled
    // template stays visible in the list and can be re-enabled via the toggle.
    params.append("status", "ALL");

    WebService.getAPI({
      action: `email-templates/get-list?${params.toString()}`,
    })
      .then((res: any) => {
        const perm = res?.data?.permissions || {};

        setPagePermission({
          canView: perm.canView,
          canAdd: perm.canAdd,
          canEdit: perm.canEdit,
          canDelete: perm.canDelete,
        });

        const formattedRows = res?.data?.data.map((item: any) => {
          return {
            ...item,
            createdAt: HelperService.getFormatedDate(item.createdAt),
            action: (
              <div>
                {perm.canEdit && (
                  <PencilFill
                    className="me-3 cursor-pointer"
                    title="Edit"
                    onClick={() => {
                      setShowForm(true);
                      setTemplateToEdit(item);
                    }}
                  />
                )}

                {perm.canEdit &&
                  (item.status === "ACTIVE" ? (
                    <ToggleOn
                      color="#16a34a"
                      size={20}
                      className="me-3 cursor-pointer"
                      title="Deactivate"
                      onClick={() => requestToggleStatus(item)}
                    />
                  ) : (
                    <ToggleOff
                      color="#9ca3af"
                      size={20}
                      className="me-3 cursor-pointer"
                      title="Activate"
                      onClick={() => requestToggleStatus(item)}
                    />
                  ))}

                {perm.canDelete && (
                  <TrashFill
                    color="#DC143C"
                    className="cursor-pointer"
                    title="Delete"
                    onClick={() => {
                      setTemplateToDelete(item._id);
                      setShowDeleteModal(true);
                    }}
                  />
                )}
              </div>
            ),
          };
        });

        setTemplates(formattedRows);
        setTotalCount(res?.data?.pagination?.total || 0);
      })
      .finally(() => setLoading(false));
  };

  // Enable/disable an email template. Disabled (INACTIVE) templates stop all
  // emails that use them from being sent (backend skips inactive templates).
  const requestToggleStatus = (item: any) => {
    setStatusItem(item);
    setShowStatusModal(true);
  };

  const confirmToggleStatus = () => {
    if (!statusItem) return;
    const newStatus = statusItem.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    WebService.putAPI({
      action: `email-templates/edit/${statusItem._id}`,
      body: { status: newStatus },
    })
      .then(() => {
        toast.success(
          newStatus === "ACTIVE"
            ? "Email template activated successfully."
            : "Email template deactivated successfully.",
        );
        setShowStatusModal(false);
        setStatusItem(null);
        getEmailTemplates(searchRef.current);
      })
      .catch(() => {
        toast.error("Failed to update email template status.");
      });
  };

  return (
    <>
      <AddEmailTemplate
        show={showForm}
        handleClose={() => {
          setShowForm(false);
          setTemplateToEdit(null);
          getEmailTemplates(searchRef.current);
        }}
        data={templateToEdit}
      />

      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={() => {
          if (templateToDelete) {
            WebService.deleteAPI({
              action: `email-templates/delete/${templateToDelete}`,
            }).then(() => {
              setShowDeleteModal(false);
              getEmailTemplates(searchRef.current);
            });
          }
        }}
        message="Are you sure you want to delete?"
      />

      <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {statusItem?.status === "ACTIVE" ? "Deactivate" : "Activate"} Email
            Template
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to{" "}
          {statusItem?.status === "ACTIVE" ? "deactivate" : "activate"} this email
          template?
          {statusItem?.status === "ACTIVE" && (
            <div className="mt-2 text-danger" style={{ fontSize: 13 }}>
              Deactivating will stop all emails that use this template from being
              sent.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button
            variant={statusItem?.status === "ACTIVE" ? "danger" : "success"}
            onClick={confirmToggleStatus}
          >
            {statusItem?.status === "ACTIVE" ? "Deactivate" : "Activate"}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="events-main">
        <Card className="event-card">
          <div className="d-flex justify-content-between">
            <div className="page-title">Email Templates</div>

            {pagePermission.canAdd && (
              <CustomButton
                isLoading={false}
                label="Add"
                onClick={() => {
                  setTemplateToEdit(null);
                  setShowForm(true);
                }}
                style={{ width: 100 }}
              />
            )}
          </div>

          <div className="row mt-2">
            <div className="col-md-3">
              <Form.Label className="label">Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter subject/template name"
                value={search}
                onChange={(e) => {
                  const value = e.target.value;
                  searchRef.current = value;                  
                  setSearch(value);
                  setCurrentPage(1);
                  getEmailTemplates(searchRef.current);
                }}
              />
            </div>
          </div>

          <CustomTableSortable
            columns={columns}
            rows={templates}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
            isLoading={loading}
            sortField={uiSortField}
            sortOrder={sortOrder}
            onSortChange={(columnId, order) => {
              setUiSortField(columnId);
              setSortOrder(order);

              const apiField = sortFieldMap[columnId] || "createdAt";
              setSortField(apiField);

              setCurrentPage(1);
              // Don't call the fetch here — sortField/sortOrder/currentPage were
              // just set and are still stale in this closure. The useEffect above
              // (which now depends on sortField/sortOrder) refetches with fresh state.
            }}
          />
        </Card>
      </div>
    </>
  );
};

export default EmailTemplates;
